import React, { useEffect, useState } from 'react';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CallIcon from '@mui/icons-material/Call';
import DirectionsIcon from '@mui/icons-material/Directions';
import Footer from './Footer';
import menu from '../menu.json';
import { track } from '../utils/analytics';
import { OPEN_COOKIE_SETTINGS } from './CookieNotice';
import shelvesPhoto from '../res/home/interior2.jpg';
import receptionPhoto from '../res/home/interior3.jpg';
import './Home.css';

const PHONE_TEL = '+918421012595';
const PHONE_DISPLAY = '84210 12595';
const WHATSAPP_URL =
  'https://wa.me/918421012595?text=' +
  encodeURIComponent("Hi Olivia Salon, I'd like to book an appointment.");
const DIRECTIONS_URL =
  'https://www.google.com/maps/dir/?api=1&destination=Olivia+Salon+%26+Academy+Balewadi';
const GOOGLE_REVIEWS_URL = 'https://www.google.com/maps?cid=1408214769096602883';
const INSTAGRAM_URL = 'https://www.instagram.com/olivia.salon16/';
const MAP_EMBED_URL =
  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3781.933860912498!2d73.77465509999999!3d18.5770203!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2b95b18f61f8f%3A0x138afbdf17763503!2sOlivia%20Salon%20%26%20Academy%20Balewadi%20-%20Hair%20Salon%20%7C%20Beauty%20Salon%20%7C%20Nail%20Salon%20%7C%20Makeup%20Studio!5e0!3m2!1sen!2sin!4v1749363749748!5m2!1sen!2sin';

const GOLD_URL =
  'https://wa.me/918421012595?text=' +
  encodeURIComponent('Hi Olivia Salon, I would like to know about Gold membership.');

// Tap tracking: spread onto a link, e.g. <a {...tap('whatsapp', 'hero')} />
const tap = (type, place) => ({ onClick: () => track('contact_tap', { type, place }) });

const whatsappUrl = (message) => 'https://wa.me/918421012595?text=' + encodeURIComponent(message);

const OPENS_AT = 10 * 60 + 30;
const CLOSES_AT = 20 * 60;

// ---------- Opening hours (always evaluated in Pune time) ----------

function getOpenStatus(now = new Date()) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const hour = Number(parts.find((p) => p.type === 'hour').value) % 24;
  const minute = Number(parts.find((p) => p.type === 'minute').value);
  const minutes = hour * 60 + minute;

  if (minutes >= OPENS_AT && minutes < CLOSES_AT) {
    return { open: true, text: 'Open now, until 8:00 pm' };
  }
  if (minutes < OPENS_AT) {
    return { open: false, text: 'Closed now, opens today at 10:30 am' };
  }
  return { open: false, text: 'Closed now, opens tomorrow at 10:30 am' };
}

// ---------- Why clients trust us (figures from the Google listing and the menu) ----------

const TRUST = [
  { title: '245 of 270', text: 'Google reviews are 5 stars, which gives us a 4.8 rating.' },
  {
    title: 'Hygiene',
    text: 'Clients mention hygiene in 25 of their reviews, and our polite, friendly staff in 32.',
  },
  {
    title: 'For women',
    text: 'Olivia is a ladies’ salon, and our whole team is women.',
  },
  {
    title: 'Privacy',
    text: 'Waxing and other personal services are done in a private area.',
  },
  {
    title: 'Products',
    text: 'We work with L’Oréal, Schwarzkopf, O3+, Cheryl’s, Oriflame and VLCC.',
  },
];

function Trust() {
  return (
    <section className="sec" aria-labelledby="trust-title">
      <div className="wrap services">
        <div className="services__intro">
          <h2 className="h-section" id="trust-title">
            Why clients trust us
          </h2>
          <p className="lede">Numbers from our Google listing, not slogans.</p>
        </div>
        <ul className="services__list">
          {TRUST.map((t) => (
            <li key={t.title} className="service">
              <h3 className="service__title">{t.title}</h3>
              <p className="service__items">{t.text}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ---------- Occasions ----------

const OCCASIONS = [
  'A hectic week',
  'A long day at work',
  'Birthday',
  'Office party',
  'A fresh new look',
  'Navaratri',
  'Sankranti',
  'Diwali',
  'Gudi Padwa',
  'Karwa Chauth',
  'Teej',
  'Raksha Bandhan',
  'Ganesh Chaturthi',
  'Holi',
  'Lohri',
  'Baisakhi',
  'Pongal',
  'Onam',
  'Durga Puja',
  'Eid',
  'Christmas',
  'New Year',
  'A traditional look',
  'A little pampering',
];

const OCCASION_STORIES = [
  {
    title: 'When work gets hectic',
    text: 'Take an hour for a head massage and let our calm, cosy salon do the rest.',
  },
  {
    title: 'When there is something to celebrate',
    text: 'Birthdays, office parties and New Year plans all look better with hair, makeup and nails done properly.',
  },
  {
    title: 'When tradition calls',
    text: 'For Navaratri, Diwali, Karwa Chauth, Teej, Eid and every other festival, we do makeup, hairstyling and saree draping.',
  },
  {
    title: 'When you want a fresh look',
    text: 'Try trendy nail art and extensions, highlights, balayage or a hair treatment that suits you.',
  },
];

function Occasions() {
  return (
    <section className="sec sec--porcelain" aria-labelledby="occasions-title">
      <div className="wrap">
        <div className="occasions__intro">
          <h2 className="h-section" id="occasions-title">
            Every important day looks better here
          </h2>
          <p className="lede">
            Women choose Olivia to feel good, whether it is a festival, a party or just a tiring
            week.
          </p>
        </div>

        <ul className="occasions__wall" aria-label="Occasions our clients visit us for">
          {OCCASIONS.map((o) => (
            <li key={o} className="occasions__item">
              {o}
            </li>
          ))}
          <li className="occasions__item occasions__item--more">and more</li>
        </ul>

        <div className="occasions__stories">
          {OCCASION_STORIES.map((s) => (
            <div key={s.title} className="story">
              <h3 className="story__title">{s.title}</h3>
              <p className="story__text">{s.text}</p>
            </div>
          ))}
        </div>

        <p className="occasions__gold">
          Gold members can pamper themselves without worrying about the bill.{' '}
          <a className="text-link" href="#gold">
            See how Gold saves you 20%
          </a>
        </p>
      </div>
    </section>
  );
}

// ---------- Services (names only, no prices) ----------

const SERVICES = [
  {
    title: 'Hair',
    topic: 'hair services',
    items: 'Haircuts, hair wash and blow dry, ironing, crimping and tong curls, L’Oréal and Schwarzkopf hair spa, global colour, root touch-up, highlights, balayage, ombré, mehandi, dandruff treatment and more',
  },
  {
    title: 'Skin',
    topic: 'facials and skin treatments',
    items: 'Facials and cleanups (O3+, Cheryl’s, Korean, Hydra, Aroma, Gold, Charcoal), bleach, D-tan and more',
  },
  {
    title: 'Hair removal',
    topic: 'threading and waxing',
    items: 'Eyebrow and face threading, Brazilian and intimate waxing, honey, Rica and roll-on wax, from under arms to full body and more',
  },
  {
    title: 'Hands, feet and nails',
    topic: 'manicure, pedicure and nails',
    items: 'Spa manicure and pedicure, gel polish, nail extensions, nail art and more',
  },
  {
    title: 'Body',
    topic: 'massage and body treatments',
    items: 'Head, foot, back, leg and full-body massage, body polishing and more',
  },
  {
    title: 'Makeup',
    topic: 'makeup and styling',
    items: 'Basic and advanced makeup, hairstyling, saree draping and more',
  },
];

function lowestPrice(serviceKey, matches) {
  const prices = (menu.services[serviceKey] || [])
    .flatMap((s) => s.types.filter((t) => matches(s, t)).map((t) => t.regular_price))
    .filter((p) => p != null);
  return prices.length ? Math.min(...prices) : null;
}

function startingPrices() {
  return [
    { label: 'Threading', price: lowestPrice('threading_and_brazilian', (s, t) => t.type === 'Threading') },
    { label: 'Haircut', price: lowestPrice('haircuts', (s) => s.name === 'Basic Haircut') },
    { label: 'Cleanup', price: lowestPrice('cleanups_and_facials', (s, t) => t.type === 'Cleanup') },
    { label: 'Facial', price: lowestPrice('cleanups_and_facials', (s, t) => t.type === 'Facial') },
  ].filter((p) => p.price != null);
}

function Services() {
  const starts = startingPrices();
  return (
    <section className="sec" id="services" aria-labelledby="services-title">
      <div className="wrap services">
        <div className="services__intro">
          <h2 className="h-section" id="services-title">
            What we do
          </h2>
          <p className="lede">
            Prices depend on things like your hair length, so message us on WhatsApp and we will share
            current rates for the service you want.
          </p>
          {starts.length > 0 && (
            <ul className="starts">
              {starts.map((p) => (
                <li key={p.label}>
                  <span className="starts__label">{p.label} from</span>
                  <span className="starts__price">{formatPrice(p.price)}</span>
                </li>
              ))}
            </ul>
          )}
          <a className="btn btn--primary" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" {...tap('whatsapp', 'services')}>
            <WhatsAppIcon aria-hidden="true" />
            Ask about a service
          </a>
        </div>
        <ul className="services__list">
          {SERVICES.map((s) => (
            <li key={s.title} className="service">
              <h3 className="service__title">{s.title}</h3>
              <div className="service__body">
                <p className="service__items">{s.items}</p>
                <a
                  className="text-link"
                  href={whatsappUrl(`Hi Olivia Salon, I'd like to know about ${s.topic}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  {...tap('whatsapp', `service_${s.title}`)}
                >
                  Ask about {s.topic}
                </a>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ---------- Reviews (written by clients on Google) ----------

const LEAD_REVIEW = {
  author: 'Renuka',
  rating: '5',
  text: 'Had a really nice experience at Olivia Salon. All the services were very good, and the facial and hair spa were especially relaxing. The staff is very polite and friendly, and the ambience is pleasant. Felt refreshed after the visit. Would surely recommend it.',
};

const SIDE_REVIEWS = [
  {
    author: 'Divya',
    rating: '5',
    text: 'I had an amazing experience at Olivia. The staff is incredibly professional, friendly, and skilled. From the moment I walked in, I was greeted warmly and made to feel comfortable.',
  },
  {
    author: 'Anusha',
    rating: '5',
    text: 'I had an amazing experience here. I got waxing and body massage done. It was up to the mark and I felt so relaxing.',
  },
  { author: 'Mrunal', rating: '5', text: 'Well organized and clean salon. I got the best hair color treatment' },
];

function Quote({ review, lead }) {
  return (
    <figure className={`quote${lead ? ' quote--lead' : ''}`}>
      <blockquote className="quote__text">&ldquo;{review.text}&rdquo;</blockquote>
      <figcaption className="quote__by">
        <strong>{review.author}</strong>
        <span>
          <span className="star" aria-hidden="true">
            ★
          </span>{' '}
          {review.rating} out of 5 on Google
        </span>
      </figcaption>
    </figure>
  );
}

const rupees = new Intl.NumberFormat('en-IN');
const formatPrice = (n) => `₹${rupees.format(n)}`;

function findPrice(serviceId, type) {
  for (const list of Object.values(menu.services)) {
    const service = list.find((s) => s.id === serviceId);
    const entry = service && service.types.find((t) => t.type === type);
    if (entry) return entry;
  }
  return null;
}

// ---------- Membership example worked out from the real menu ----------

function MembershipExample() {
  const wax = findPrice('body_waxing_full_leg', 'Rica');
  const pedicure = findPrice('manicure_pedicure_spa_pedicure', 'Regular');
  if (!wax || !pedicure) return null;

  const regular = wax.regular_price + pedicure.regular_price;
  const member = wax.gold_price + pedicure.gold_price;

  return (
    <div className="member__example">
      <h3>A worked example</h3>
      <div className="member__sum">
        <div className="member__line">
          <span>Full leg waxing (Rica)</span>
          <span>{formatPrice(wax.regular_price)}</span>
        </div>
        <div className="member__line">
          <span>Spa pedicure</span>
          <span>{formatPrice(pedicure.regular_price)}</span>
        </div>
        <div className="member__line">
          <span>Regular total</span>
          <span>{formatPrice(regular)}</span>
        </div>
        <div className="member__line member__line--total">
          <span>Gold member total</span>
          <span>{formatPrice(member)}</span>
        </div>
      </div>
      <p className="lede">You save {formatPrice(regular - member)} on this visit.</p>
    </div>
  );
}

// ---------- FAQ (also published as structured data for Google) ----------

const FAQS = [
  {
    q: 'How do I book an appointment?',
    a: 'Message us on WhatsApp or call 84210 12595. We recommend booking in advance so the stylist you want is free.',
  },
  {
    q: 'What are your opening hours?',
    a: 'We are open every day from 10:30 am to 8:00 pm.',
  },
  {
    q: 'How much will my service cost?',
    a: 'It depends on the service and on things like your hair length. Message us on WhatsApp with what you want and we will share current rates.',
  },
  {
    q: 'Who will do my service, and is it private?',
    a: 'Our whole team is women. Waxing and other personal services, including intimate waxing, are done in a private area.',
  },
  {
    q: 'How can I pay?',
    a: 'We accept cash, UPI, credit cards and debit cards.',
  },
  {
    q: 'What is Gold membership?',
    a: 'Gold members get 20% off our services, and the membership can be shared with family and friends at no extra cost. There is no limit on how many times you visit, monthly or annually. Message or call us and we will explain how it works.',
  },
  {
    q: 'Where are you, and which languages do you speak?',
    a: 'We are at Shop No 11, Kaizen Society, Sopan Baug Road, near F Residences, Balewadi, Pune 411045. We speak English, Hindi and Marathi.',
  },
];

function Faq() {
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <section className="sec sec--porcelain" aria-labelledby="faq-title">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
      <div className="wrap services">
        <div className="services__intro">
          <h2 className="h-section" id="faq-title">
            Before you visit
          </h2>
          <p className="lede">Quick answers to what new clients usually ask.</p>
        </div>
        <div className="faq">
          {FAQS.map((f) => (
            <details key={f.q} className="faq__item">
              <summary className="faq__q">{f.q}</summary>
              <p className="faq__a">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------- Page ----------

const Home = () => {
  const [status, setStatus] = useState(() => getOpenStatus());

  useEffect(() => {
    const id = setInterval(() => setStatus(getOpenStatus()), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="home">
      <main>
      {/* Hero */}
      <header className="hero on-dark">
        <div className="wrap hero__grid">
          <div className="hero__copy">
            <h1 className="h-display">
              Clean, friendly, top-rated. And kind to your purse.
            </h1>
            <p className="lede">
              Olivia Salon &amp; Academy has served Balewadi, Pune, as a ladies&rsquo; salon and beauty parlour since 2023, for hair, skin, nails, waxing and makeup. Looking this good should not cost a fortune, and Gold members get 20% off.
            </p>
            <div className="hero__actions">
              <a className="btn btn--primary" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" {...tap('whatsapp', 'hero')}>
                <WhatsAppIcon aria-hidden="true" />
                Book on WhatsApp
              </a>
              <a className="btn btn--ghost" href={`tel:${PHONE_TEL}`} {...tap('call', 'hero')}>
                <CallIcon aria-hidden="true" />
                Call {PHONE_DISPLAY}
              </a>
              <a className="text-link" href="#services">
                See our services
              </a>
            </div>
            <div className="hero__facts">
              <div className="hero__fact">
                <span className={`status-dot${status.open ? ' status-dot--open' : ''}`} aria-hidden="true" />
                <span>{status.text}</span>
              </div>
              <div className="hero__fact">
                <span className="star" aria-hidden="true">
                  ★
                </span>
                <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer">
                  Rated 4.8 on Google from 270 reviews
                </a>
              </div>
            </div>
          </div>

          <div className="mirror mirror--glow hero__photo">
            <img
              className="mirror__img"
              src={`${process.env.PUBLIC_URL}/hero-stations.jpg`}
              alt="Styling stations at Olivia Salon in Balewadi, with black chairs and tall mirrors framed in warm light"
              width="1100"
              height="1467"
              fetchpriority="high"
            />
          </div>
        </div>
      </header>

      <Trust />

      <Occasions />

      <Services />

      {/* The salon */}
      <section className="sec sec--porcelain" aria-labelledby="salon-title">
        <div className="wrap visit-salon">
          <div className="visit-salon__copy">
            <h2 className="h-section" id="salon-title">
              Come in and look around
            </h2>
            <p className="lede">
              We are in Kaizen Society on Sopan Baug Road, near F Residences. These photos were taken
              inside the salon.
            </p>
            <p>
              <a className="text-link" href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer">
                See photos of our work on Google
              </a>
            </p>
            <p>
              <a className="text-link" href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
                Follow us on Instagram
              </a>
            </p>
          </div>
          <div className="visit-salon__photos">
            <figure className="shot">
              <div className="mirror shot__frame">
                <img
                  className="mirror__img"
                  src={receptionPhoto}
                  alt="Reception desk with the glowing Olivia lotus logo on the wall"
                  width="1100"
                  height="1467"
                  loading="lazy"
                />
              </div>
            </figure>
            <figure className="shot shot--drop">
              <div className="mirror shot__frame">
                <img
                  className="mirror__img"
                  src={shelvesPhoto}
                  alt="Waiting bench beside lit shelves of professional hair and skin products"
                  width="1100"
                  height="1467"
                  loading="lazy"
                />
              </div>
            </figure>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="sec" aria-labelledby="reviews-title">
        <div className="wrap">
          <h2 className="h-section" id="reviews-title">
            What clients wrote on Google
          </h2>
          <div className="reviews__grid">
            <Quote review={LEAD_REVIEW} lead />
            <div className="reviews__side">
              {SIDE_REVIEWS.map((r) => (
                <Quote key={r.author} review={r} />
              ))}
            </div>
          </div>
          <p className="reviews__foot">
            <a className="text-link" href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer">
              Read all reviews on Google
            </a>
          </p>
        </div>
      </section>

      {/* Membership */}
      <section className="sec sec--ink on-dark" id="gold" aria-labelledby="gold-title">
        <div className="wrap member">
          <div className="member__copy">
            <h2 className="h-section" id="gold-title">
              A top salon should not break the purse. Gold takes 20% off.
            </h2>
            <p className="lede">
              Gold members get 20% off our services, and the membership can be shared with family and
              friends at no extra cost. There is no limit on how many times you visit, monthly or
              annually. Message or call us and we will explain how it works.
            </p>
            <a className="btn btn--ghost" href={GOLD_URL} target="_blank" rel="noopener noreferrer" {...tap('whatsapp', 'gold')}>
              <WhatsAppIcon aria-hidden="true" />
              Ask about Gold membership
            </a>
          </div>
          <MembershipExample />
        </div>
      </section>

      <Faq />

      {/* Visit */}
      <section className="sec" id="visit" aria-labelledby="visit-title">
        <div className="wrap find">
          <div className="find__copy">
            <h2 className="h-section" id="visit-title">
              Visit us in Balewadi
            </h2>
            <dl className="facts">
              <div>
                <dt>Address</dt>
                <dd>
                  Shop No 11, Kaizen Society, Sopan Baug Road, near F Residences, Balewadi, Pune,
                  Maharashtra 411045
                </dd>
              </div>
              <div>
                <dt>Hours</dt>
                <dd>Every day, 10:30 am to 8:00 pm</dd>
              </div>
              <div>
                <dt>Phone and WhatsApp</dt>
                <dd>
                  <a href={`tel:${PHONE_TEL}`} {...tap('call', 'visit')}>
                    {PHONE_DISPLAY}
                  </a>
                </dd>
              </div>
              <div>
                <dt>We speak</dt>
                <dd>English, Hindi and Marathi</dd>
              </div>
              <div>
                <dt>You can pay by</dt>
                <dd>Cash, UPI, credit card or debit card</dd>
              </div>
            </dl>
            <div className="find__actions">
              <a className="btn btn--primary" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" {...tap('whatsapp', 'visit')}>
                <WhatsAppIcon aria-hidden="true" />
                Book on WhatsApp
              </a>
              <a className="btn btn--line" href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" {...tap('directions', 'visit')}>
                <DirectionsIcon aria-hidden="true" />
                Get directions
              </a>
            </div>
          </div>
          <div className="find__map">
            <iframe
              src={MAP_EMBED_URL}
              title="Map showing Olivia Salon & Academy in Balewadi"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      </main>

      <Footer />

      <div className="cookie-strip">
        <button
          type="button"
          className="cookie-strip__btn"
          onClick={() => window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS))}
        >
          Cookie settings
        </button>
      </div>

      {/* Phone-only action bar, always within thumb reach */}
      <div className="bar" role="group" aria-label="Contact us">
        <a className="btn btn--primary" href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" {...tap('whatsapp', 'bar')}>
          <WhatsAppIcon aria-hidden="true" />
          Book
        </a>
        <a className="btn btn--ghost" href={`tel:${PHONE_TEL}`} {...tap('call', 'bar')}>
          <CallIcon aria-hidden="true" />
          Call
        </a>
        <a className="btn btn--ghost" href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" {...tap('directions', 'bar')}>
          <DirectionsIcon aria-hidden="true" />
          Map
        </a>
      </div>
      <div className="bar__space" aria-hidden="true" />
    </div>
  );
};

export default Home;
